import React from 'react'
import { useAllFoundersQuery } from '../../hooks/useAllFoundersQuery'
import { Title } from '../archive/Title'
import { Carousel } from '../carousel/Carousel'
import { Link } from '../global'
import { shuffleArray } from '../../utils'

import './foundersClub.scss'

/**
 * Generate an array of JSX spans for titles, separated by commas and "och" for the last item.
 *
 * @param {Array} data - The array of title data to be processed.
 * @returns {Array} An array of JSX spans for titles with separators.
 */
const generateTitleSpans = data => {
  const titles = []

  for (let i = 0; i < data.length; i++) {
    const element = data[i]
    if (i === data.length - 1) titles.push(' och ')
    else if (i) titles.push(', ')

    titles.push(<span key={i}>{element.title}</span>)
  }

  return titles
}

/**
 * generate founder data as links containing images and descriptions (if available).
 *
 * @param {Array} data - The array of founder data to be rendered.
 * @returns {Array} An array of JSX elements representing founder data as links.
 */
const generateFounderDataAsLinks = data =>
  data.map(({ description, link, src, altText, title, objectFit }, index) => {
    return (
      <Link key={index} href={link?.url || null} target={link?.target}>
        <div className={`size ${objectFit}`}>
          {description && (
            <div className="founder-description">
              <div>{title}</div>
              <p>{description}</p>
            </div>
          )}
          <img src={src} alt={altText || title} style={{ objectFit }} />
        </div>
      </Link>
    )
  })

/**
 * Represents the Founders Club component, showcasing company founders who have contributed to the page.
 *
 * @param {boolean} props.showContact - Whether to display contact button for potential contributors (optional, default is `false`).
 * @param {boolean} props.carousel - Whether to display the founder images in a carousel format (optional, default is `false`).
 *
 * @returns {JSX.Element} A React component rendering the Founders Club section.
 */
export const FoundersClub = ({
  area,
  showContact = false,
  carousel = false,
}) => {
  const data = useAllFoundersQuery(area)
  if (area === 'arkitektvarlden') return null

  const shuffledAndFilteredData =
    data?.length && shuffleArray(data).filter(obj => obj.src)

  const titles = generateTitleSpans(shuffledAndFilteredData)
  const images = generateFounderDataAsLinks(shuffledAndFilteredData)
  const wordMap = {
    betongvarlden: 'Betongvärlden',
    arkitektvarlden: 'Arkitektvärlden',
  }

  const transformWord = str => {
    if (!str) return str

    if (wordMap[str.toLowerCase()]) {
      return wordMap[str.toLowerCase()]
    }

    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  return (
    <div className="founders-club">
      <div className="innerContainer">
        <Title text={area === 'betongvarlden' ? 'Betongklubben' : 'Partners'} />
        <p>
          {transformWord(area)} är beroende av att företagen i branschen
          engagerar sig och bidrar ekonomiskt. Därför tackar vi företagen som
          engagerar sig tillsammans med oss: {titles}.
        </p>

        {carousel && (
          <div className={`founder-carusel`}>
            <Carousel
              data={images}
              autoPlay
              settings={{
                responsive: [
                  {
                    breakpoint: 768,
                    settings: {
                      swipeToSlide: true,
                    },
                  },
                ],
              }}
            />
          </div>
        )}

        <div className={`image-container ${carousel ? 'hide' : ''}`}>
          {images}
        </div>

        {showContact && (
          <div className="contact-container">
            <p>Är du också intresserad av att synas här?</p>
            <Link to={`/annonsera`}>Kontakta oss</Link>
          </div>
        )}
      </div>
    </div>
  )
}
